import {
  FluidCustomValidationConfiguration,
  FluidDateFieldValidation,
  FluidDependentFieldConfiguration,
  FluidDependentFieldValidation,
  FluidFieldValidation,
  FluidFieldValidationType,
  FluidFormElementState,
  FluidFormElementTranslationOptions,
  FluidFormState,
  FluidFormTokens,
  FluidPrimitive,
  isNullOrUndefined,
  replaceTemplate,
} from '@lmig/fluid-core';
import { addDays, isAfter, isBefore, subDays } from 'date-fns';

import { FluidI18nStore } from '../../providers/fluid-i18n-provider/store/fluid-i18n.store';
import { Utils } from '../utils';
import {
  dateFromString,
  validateDaysBeforeAfter,
  validateMinMaxDate,
} from './date-validation.utils';

/**
 * Runs all validators against the given element state.
 * @param elementState
 * @param validation
 * @param parentState
 * @param externalState
 */
export const validate = (
  elementState: FluidFormElementState,
  validation: FluidFieldValidation[],
  parentState?: FluidFormState,
  externalState?: Record<string, any>
) => {
  return (validation || []).reduce(
    (errors: any[], validator: FluidFieldValidation) => {
      if (
        !elementValueIsValid(
          validator.type,
          elementState,
          validator.value,
          validator.baseValue,
          (validator as FluidDateFieldValidation).format,
          (validator as FluidDependentFieldValidation).dependentField,
          parentState,
          validator as FluidCustomValidationConfiguration,
          externalState,
          validator.comparatorPath
        )
      ) {
        errors.push({
          ...validator,
          message: applyReplacements(validator, parentState),
        });
      }
      return errors;
    },
    []
  );
};

/**
 * Updates the validation message for a given validator with any token replacements.
 */
type FormMessageReplacerToken = { token: string; replacement: FluidPrimitive };
export const applyReplacements = (
  validator: FluidFieldValidation,
  parentState: FluidFormState
) => {
  const getReplacement = (
    value: FluidPrimitive,
    tokenFormatter: (replacement: any) => any
  ) => (tokenFormatter ? tokenFormatter(value) : value);

  const replacerTokens = [
    {
      token: FluidFormTokens.COMPARATOR,
      replacement: getReplacement(
        parentState?.rawValue?.[validator.comparatorPath] as FluidPrimitive,
        validator.tokenFormatters?.[FluidFormTokens.COMPARATOR]
      ),
    },
  ];
  return messageHasTokens(replacerTokens, validator.message)
    ? replaceAllTokens(replacerTokens, validator.message)
    : validator.message;
};

/**
 * Replaces all instances of form replacement tokens with their corresponding
 * values in a given base template.
 * @param replacerTokens
 * @param baseTemplate
 */
function replaceAllTokens(
  replacerTokens: FormMessageReplacerToken[],
  baseTemplate = ''
): string {
  return replacerTokens?.reduce(
    (finalString: string, currentToken: FormMessageReplacerToken) => {
      return replaceTemplate(
        currentToken?.token,
        finalString,
        currentToken.replacement as string | number
      );
    },
    baseTemplate
  );
}

/**
 * Checks if the given message has any tokens before we attempt to replace them.
 * @param replacerTokens
 * @param message
 */
const messageHasTokens = (
  replacerTokens: FormMessageReplacerToken[] = [],
  message: string
) =>
  replacerTokens?.some(
    (replacerToken: FormMessageReplacerToken) =>
      message?.indexOf(replacerToken.token) !== -1
  );

export const elementValueIsValid = (
  type: FluidFieldValidationType,
  elementState: FluidFormElementState,
  requiredValue: any,
  baseValue?: any,
  format?: string,
  dependentField?: FluidDependentFieldConfiguration,
  parentValue?: FluidFormState,
  customValidation?: FluidCustomValidationConfiguration,
  externalState?: Record<string, any>,
  comparatorPath?: string
): boolean => {
  switch (type) {
    case FluidFieldValidationType.REQUIRED:
      return !isNullOrUndefined(elementState?.value, true);
    case FluidFieldValidationType.MIN_VALUE:
      return (elementState?.value as number) >= requiredValue;
    case FluidFieldValidationType.MAX_VALUE:
      return (elementState?.value as number) <= requiredValue;
    case FluidFieldValidationType.MAX_LENGTH:
      return (elementState?.value as string)?.length <= requiredValue;
    case FluidFieldValidationType.MIN_LENGTH:
      return (elementState?.value as string)?.length >= requiredValue;
    case FluidFieldValidationType.DAYS_AFTER:
      return validateDaysBeforeAfter(
        dateFromString(elementState?.value),
        requiredValue,
        baseValue,
        isBefore,
        addDays,
        format
      );
    case FluidFieldValidationType.DAYS_BEFORE:
      return validateDaysBeforeAfter(
        dateFromString(elementState?.value),
        requiredValue,
        baseValue,
        isAfter,
        subDays,
        format
      );
    case FluidFieldValidationType.MAX_DATE:
      return validateMinMaxDate(
        dateFromString(elementState?.value),
        requiredValue,
        isBefore,
        format
      );
    case FluidFieldValidationType.MIN_DATE:
      return validateMinMaxDate(
        dateFromString(elementState?.value),
        requiredValue,
        isAfter,
        format
      );
    case FluidFieldValidationType.PATTERN:
      return new RegExp(requiredValue).test(elementState?.value as string);
    case FluidFieldValidationType.DEPENDENT:
      return validateDependentField(
        elementState?.value,
        dependentField,
        parentValue
      );
    case FluidFieldValidationType.CUSTOM:
      return validateCustom(
        elementState?.value,
        customValidation,
        parentValue,
        externalState
      );
    case FluidFieldValidationType.IS_AFTER_FIELD:
      return validateAgainstOtherDateField(
        elementState?.value,
        comparatorPath,
        parentValue,
        FluidFieldValidationType.IS_AFTER_FIELD
      );
    case FluidFieldValidationType.IS_BEFORE_FIELD:
      return validateAgainstOtherDateField(
        elementState?.value,
        comparatorPath,
        parentValue,
        FluidFieldValidationType.IS_BEFORE_FIELD
      );
  }
};

/**
 * Validates the selected date against the date selected in another field to
 * check that it is before or after the date.
 * @param value
 * @param dateField
 * @param parentState
 * @param validator
 */
export const validateAgainstOtherDateField = (
  value: string,
  dateField: string,
  parentState: FluidFormState,
  validator:
    | FluidFieldValidationType.IS_AFTER_FIELD
    | FluidFieldValidationType.IS_BEFORE_FIELD
): boolean => {
  if (!dateField) {
    Utils.consoleWarn(
      { tagName: 'FLUID-FORM' } as HTMLElement,
      'Before/After validator configured without a comparatorPath - cannot apply validation'
    );
    return true;
  }
  const comparatorDate: string = parentState?.rawValue?.[dateField] as string;
  const dateFn =
    validator === FluidFieldValidationType.IS_AFTER_FIELD ? isAfter : isBefore;
  return comparatorDate && value
    ? dateFn(new Date(value), new Date(comparatorDate))
    : true;
};

export const validateDependentField = (
  value: any,
  dependentField: FluidDependentFieldConfiguration,
  parentValue: FluidFormState
) => {
  const dependentFieldMatches =
    parentValue?.value[dependentField.controlName].value ===
    dependentField.value;
  return dependentField.validatorFn(dependentFieldMatches, value);
};

export const validateCustom = (
  value: any,
  customValidation: FluidCustomValidationConfiguration,
  parentValue?: FluidFormState,
  externalState?: Record<string, any>
) => {
  if (customValidation.dependentField) {
    return validateCustomDependent(
      value,
      customValidation.dependentField,
      parentValue
    );
  }
  return customValidation.validatorFn(value, parentValue, externalState);
};

export const validateCustomDependent = (
  value: any,
  dependentField: FluidDependentFieldConfiguration,
  parentValue: FluidFormState
) => {
  if (!parentValue) return true;
  const dependentFieldValue =
    parentValue?.value[dependentField.controlName]?.value;
  return dependentField.customValidatorFn(dependentFieldValue, value);
};

export const isRequired = (validation: FluidFieldValidation[]) => {
  return !!(validation || []).find(
    (validation: FluidFieldValidation) =>
      validation.type === FluidFieldValidationType.REQUIRED
  );
};

export const hasError = (
  elementState: FluidFormElementState,
  touched: boolean
) => {
  return elementState?.errors?.length > 0 && (touched || !!elementState?.value);
};

export const getErrorMessage = (
  elementState: FluidFormElementState,
  fluidTranslate?: FluidFormElementTranslationOptions,
  i18nStore?: FluidI18nStore,
  formState?: any,
  externalState?: any
) => {
  const applicableError: FluidFieldValidation = elementState?.errors[0];
  const translationKey: string =
    fluidTranslate?.validation?.[applicableError?.id];
  const translatedError = i18nStore?.translate(
    applicableError?.fluidTranslate?.message || translationKey,
    { value: elementState?.value, formState, externalState }
  );
  return translatedError || elementState?.errors[0]?.message;
};
